import ChatExperience from './components/ChatExperience';
import { catalogue } from './data/formulas';
import './styles/app.css';

const howItWorks = [
  {
    title: '01. Guided intake, not a diagnosis',
    description:
      'Soupcon listens for TCM patterns using your own words. We surface likely matches while reminding you to escalate any red flag symptoms to your physician.'
  },
  {
    title: '02. Practitioner review on every plan',
    description:
      'Licensed herbalists vet each AI-drafted recommendation, adjust dosages, and confirm there are no herb-drug conflicts before you subscribe.'
  },
  {
    title: '03. Subscription with human check-ins',
    description:
      'Receive refills on your cadence, pause any time, and unlock optional chat consults for deeper coaching when something shifts.'
  }
];

const plans = [
  {
    name: 'Foundations',
    price: '$68',
    cadence: 'per month',
    features: [
      'Curated single-classic formula shipped every 30 days',
      'Wellness journal + reminders to track progress',
      'Practitioner email review within 24 hours' 
    ]
  },
  {
    name: 'Deep dive',
    price: '$108',
    cadence: 'per month',
    highlight: 'Popular',
    features: [
      'Alternating formulas to match cycle or seasonal shifts',
      'Biweekly practitioner chat nudges',
      'Emergency escalation channel for safety questions'
    ]
  },
  {
    name: 'Concierge',
    price: '$189',
    cadence: 'per month',
    features: [
      'Custom compounding with optional add-on herbs',
      'Live video consults each month',
      'Lab and medication review with integrative team'
    ]
  }
];

const faqs = [
  {
    question: 'Is Soupcon giving me a prescription?',
    answer:
      'No. We operate as an education-forward concierge that explains how existing patents may fit your presentation. A licensed clinician must approve every shipment and can advise when to seek in-person medical care.'
  },
  {
    question: 'How custom are the formulas?',
    answer:
      'We begin with CGMP-vetted classical formulas then layer practitioner adjustments (dosage tweaks, add-on herbs) when appropriate. Full custom powders are limited to concierge members and always reviewed by a human.'
  },
  {
    question: 'Can I change or pause my deliveries?',
    answer:
      'Absolutely. Manage cadence, pause, or request check-ins from your dashboard. We prompt you before each refill to confirm everything still feels right.'
  },
  {
    question: 'What if I am already on medication?',
    answer:
      'Flag it during intake. We maintain an interaction database and escalate complex cases to practitioners experienced with collaborative care.'
  }
];

const Footer = () => (
  <footer className="site-footer section-shell">
    <div>
      <h4>Soupcon</h4>
      <p>AI-guided traditional Chinese medicine, always with human oversight.</p>
    </div>
    <div className="footer-links">
      <a href="#experience">AI Intake</a>
      <a href="#plans">Plans</a>
      <a href="#faq">FAQ</a>
      <a href="#">Privacy</a>
      <a href="#">Terms</a>
    </div>
    <p className="legal">These statements have not been evaluated by the FDA. Not intended to diagnose, treat, cure, or prevent any disease.</p>
  </footer>
);

const App = () => {
  return (
    <div className="layout">
      <header className="site-header glass-panel">
        <div className="section-shell header-content">
          <div className="brand">
            <span className="logo-glyph">S</span>
            <div>
              <strong>Soupcon</strong>
              <span className="tagline">TCM insight, modern experience.</span>
            </div>
          </div>
          <nav className="nav-links">
            <a href="#experience">Experience</a>
            <a href="#library">Library</a>
            <a href="#plans">Plans</a>
            <a href="#faq">FAQ</a>
            <a href="/admin">Team console</a>
            <button className="btn btn-primary">Start intake</button>
          </nav>
        </div>
      </header>

      <section className="hero section-shell">
        <div className="hero-copy">
          <span className="tag-pill">Traditional wisdom - AI-supported</span>
          <h1>Meet your modern TCM concierge.</h1>
          <p>
            Soupcon helps you articulate what your body is telling you, maps it to
            time-tested formulas, and keeps every recommendation grounded in licensed
            practitioner review. Personalised without crossing into prescription territory.
          </p>
          <div className="cta-group">
            <a className="btn btn-primary" href="#experience">Begin the guided chat</a>
            <a className="btn btn-secondary" href="#plans">See subscription plans</a>
          </div>
          <ul className="hero-highlights">
            <li>Story-first intake capturing tongue, temperature, and lifestyle clues.</li>
            <li>Recommendation logic with clinician oversight and transparent rationale.</li>
            <li>No subscription billed until you approve the plan preview.</li>
          </ul>
        </div>
        <div className="hero-visual glass-panel">
          <div className="hero-card">
            <span className="pill">Today's focus -- Libido & warmth</span>
            <h3>"My spark dipped once winter hit."</h3>
            <p>
              Soupcon analyses intake markers and suggests kidney yang support with warming
              lifestyle additions, queuing a practitioner to confirm dosing before shipment.
            </p>
            <div className="hero-metrics">
              <div>
                <strong>12h</strong>
                <span>average review turnaround</span>
              </div>
              <div>
                <strong>68%</strong>
                <span>report feeling seen after first chat</span>
              </div>
              <div>
                <strong>0</strong>
                <span>automated prescriptions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main>
        <section className="section-shell" id="how-it-works">
          <div className="how-grid glass-panel">
            {howItWorks.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <ChatExperience />

        <section className="section-shell" id="library">
          <div className="library glass-panel">
            <header>
              <span className="tag-pill">Inventory transparency</span>
              <h2>Formulas we keep on hand</h2>
              <p>
                GMP-certified suppliers, batch tracking, and immediate substitution if a lot
                doesn't pass our sensory or potency checks.
              </p>
            </header>
            <div className="library-grid">
              {catalogue.map((formula) => (
                <article key={formula.id}>
                  <h4>{formula.name}</h4>
                  <p>{formula.pattern}</p>
                  <ul>
                    {formula.lifestyleTips.slice(0, 2).map((tip) => (
                      <li key={tip}>{tip}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section-shell" id="plans">
          <div className="plans-grid">
            {plans.map((plan) => (
              <article key={plan.name} className={plan.highlight ? 'plan-card highlight' : 'plan-card'}>
                {plan.highlight && <span className="plan-highlight">{plan.highlight}</span>}
                <h3>{plan.name}</h3>
                <p className="price">
                  {plan.price} <span>{plan.cadence}</span>
                </p>
                <ul>
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <button className="btn btn-primary">Request preview</button>
              </article>
            ))}
          </div>
        </section>

        <section className="section-shell faq" id="faq">
          <header>
            <span className="tag-pill">Honest answers</span>
            <h2>Frequently asked questions</h2>
          </header>
          <div className="faq-grid glass-panel">
            {faqs.map((faq) => (
              <article key={faq.question}>
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default App;
